import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useRemoteParticipants } from "@livekit/components-react";
import { useTranslate } from "@tolgee/react";
import { ChevronDown, Hand } from "lucide-react";
import { useAuthService } from "../../Context/AuthContext";
import { colors, rgb } from "../../Assets/styles/colors";

const RaisedHandsContext = createContext(new Set());

export const RaisedHandsProvider = ({ raisedHands, children }) => {
  const identities = useMemo(
    () => new Set(raisedHands.map((hand) => String(hand.user_id))),
    [raisedHands]
  );

  return (
    <RaisedHandsContext.Provider value={identities}>
      {children}
    </RaisedHandsContext.Provider>
  );
};

export const useIsHandRaised = (identity) =>
  useContext(RaisedHandsContext).has(String(identity));

export const RaisedHandIcon = ({ identity }) => {
  const handRaised = useIsHandRaised(identity);
  if (!handRaised) {
    return null;
  }

  return (
    <RaisedHandIconWrapper>
      <Hand size={16} />
    </RaisedHandIconWrapper>
  );
};

export const RaisedHandsIndicator = ({ raisedHands }) => {
  const { t } = useTranslate();
  const { user } = useAuthService();
  const remoteParticipants = useRemoteParticipants();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const isSelf = (hand) => String(hand.user_id) === String(user?.id);

  const hands = useMemo(() => {
    const inRoom = new Set(remoteParticipants.map((participant) => participant.identity));
    return raisedHands.filter((hand) => isSelf(hand) || inRoom.has(String(hand.user_id)));
  }, [raisedHands, remoteParticipants, user?.id]);

  const hasMultiple = hands.length > 1;

  useEffect(() => {
    if (!hasMultiple) {
      setOpen(false);
    }
  }, [hasMultiple]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleOutsideClick = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  if (hands.length === 0) {
    return null;
  }

  const summary = () => {
    if (!hasMultiple) {
      return isSelf(hands[0])
        ? t("call_modal.raised_hand_you")
        : t("call_modal.raised_hand_single", { name: hands[0].name });
    }
    const featured = [...hands].reverse().find((hand) => !isSelf(hand));
    const others = hands.length - 1;
    return others === 1
      ? t("call_modal.raised_hand_one_other", { name: featured.name })
      : t("call_modal.raised_hand_many_others", { name: featured.name, count: others });
  };

  const text = summary();

  return (
    <IndicatorWrapper ref={wrapperRef}>
      <IndicatorPill
        type="button"
        title={text}
        $clickable={hasMultiple}
        aria-expanded={hasMultiple ? open : undefined}
        onClick={() => hasMultiple && setOpen((value) => !value)}
      >
        <Hand size={16} />
        <IndicatorText>{text}</IndicatorText>
        {hasMultiple && <IndicatorCount>{hands.length}</IndicatorCount>}
        {hasMultiple && <Chevron size={16} $open={open} />}
      </IndicatorPill>

      {open && (
        <Dropdown data-raised-hands-open>
          {hands.map((hand) => (
            <DropdownRow key={hand.user_id}>
              <Hand size={14} />
              <DropdownName title={hand.name}>
                {hand.name}
                {isSelf(hand) && ` (${t("call_modal.you")})`}
              </DropdownName>
            </DropdownRow>
          ))}
        </Dropdown>
      )}
    </IndicatorWrapper>
  );
};

const RaisedHandIconWrapper = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: ${colors.raisedHandYellow};
  pointer-events: none;
`;

const IndicatorWrapper = styled.div`
  position: relative;
  flex: 0 1 auto;
  min-width: 0;
`;

const IndicatorPill = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 250px;
  width: 100%;
  height: 32px;
  box-sizing: border-box;
  padding: 0 10px;
  border: none;
  border-radius: 4px;
  background: rgba(${rgb(colors.raisedHandYellow)}, 0.15);
  color: ${colors.raisedHandYellow};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  svg {
    flex-shrink: 0;
  }
`;

const IndicatorText = styled.span`
  overflow: hidden;
  font-size: 13px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.menu.primaryTextColor};

  @container callHeader (max-width: 560px) {
    display: none;
  }
`;

const IndicatorCount = styled.span`
  display: none;
  font-size: 13px;
  font-weight: 600;

  @container callHeader (max-width: 560px) {
    display: inline;
  }
`;

const Chevron = styled(ChevronDown)`
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  transition: transform 0.15s ease;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-sizing: border-box;
  width: 250px;
  max-height: 260px;
  overflow-y: auto;
  padding: 6px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.groupContentBackground};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
`;

const DropdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  color: ${colors.raisedHandYellow};

  svg {
    flex-shrink: 0;
  }
`;

const DropdownName = styled.span`
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;
